// WordCloud functionality using wordcloud2.js
import WordCloud from 'wordcloud'
import { resolveSentimentColors, sentimentColor, cssColor } from './sentiment_colors'
import {
  EXPORT_BACKGROUND,
  EXPORT_PADDING,
  buildWordcloudFilename,
  contentBounds,
  exportFrame,
  exportLayout
} from '../lib/wordcloud_export'

// 品詞色 / 感情色の切り替え状態。正は画面上のトグルで、描画のたびに DOM から取り直す
let colorMode = 'hinshi'

// 対象切替(全体 / ポジティブ / ネガティブ)の空状態メッセージ用
const FOCUS_LABELS = { positive: 'ポジティブ', negative: 'ネガティブ' }
// 「全体」表示でのメインキーワードの色(凡例「キーワード自身」と同系の赤)
const MAIN_WORD_COLOR = '#f31260'
// ポジ/ネガ単色表示では赤がネガ色と衝突するため、メインキーワードだけ専用色にする
const MAIN_WORD_CSS_VARIABLE = '--tm-wordcloud-main'
// PNG 書き出し(白背景固定)でのポジ/ネガ表示のメインキーワード色。--tm-wordcloud-main のライトテーマの値
const EXPORT_MAIN_WORD_COLOR = '#111827'
// 書き出し用の描き直しが wordcloudstop を返さなかったときに打ち切る時間
const EXPORT_TIMEOUT_MS = 15000

// PNG 保存ボタンは描き終わるまで無効にする(wordcloud2 は数フレームに分けて描くため、途中で押すと描きかけの画像になる)。
// 対象の切替で語が 0 件になったとき・描画に失敗したときも無効のまま
const setDownloadReady = (element, ready) => {
  element.dataset.wordcloudReady = String(ready)
  const button = (element.closest('.card-body') || document).querySelector('button[data-wordcloud-download]')
  if (button) button.disabled = !ready
}

class WordCloudRenderer {
  constructor() {
    this.init()
  }

  init() {
    document.addEventListener('turbo:load', () => {
      this.renderWordClouds()
    })
  }

  renderWordClouds() {
    // data-wordcloud属性を持つ要素を処理
    document.querySelectorAll('[data-wordcloud]').forEach((element) => this.renderWordCloudFor(element))
  }

  // 対象(全体 / ポジティブ / ネガティブ)は要素の dataset に持たせる。既定は全体
  renderWordCloudFor(element) {
    setDownloadReady(element, false)

    // 色モードの単一ソースは画面上のトグル。Turbo 遷移でトグルが初期状態に戻るため、描画のたびに DOM から同期する
    colorMode = document.querySelector('[data-sentiment-color-toggle]')?.checked ? 'sentiment' : 'hinshi'

    const focus = element.dataset.wordcloudFocus || 'all'
    const data = this.dataFor(element, focus)
    const mainWord = element.getAttribute('data-main-word')

    if (!data) {
      element.innerHTML = '<p class="text-gray-500 p-4">ワードクラウドデータがありません</p>'
      return
    }

    try {
      const wordData = JSON.parse(data)
      if (focus !== 'all' && (!wordData || wordData.length === 0)) {
        element.innerHTML = `<p class="text-base-content/70 text-center">${FOCUS_LABELS[focus] || ''}な投稿に含まれる語がありません</p>`
        return
      }

      this.createWordCloud(element, wordData, mainWord, this.sentimentColors(element), focus)
    } catch (error) {
      console.error('WordCloud data parsing error:', error)
      element.innerHTML = '<p class="text-red-500 p-4">データの解析エラー</p>'
    }
  }

  dataFor(element, focus) {
    if (focus === 'positive') return element.getAttribute('data-wordcloud-positive')
    if (focus === 'negative') return element.getAttribute('data-wordcloud-negative')

    return element.getAttribute('data-wordcloud')
  }

  // data-sentiment-classes( { 語: "positive" } )を実色に解決する。内訳を持たないソースでは属性自体が無い
  sentimentColors(element) {
    const raw = element.getAttribute('data-sentiment-classes')
    if (!raw) return null

    try {
      return resolveSentimentColors(JSON.parse(raw))
    } catch (error) {
      console.error('WordCloud sentiment classes parsing error:', error)
      return null
    }
  }

  createWordCloud(element, wordData, mainWord = null, sentimentColors = null, focus = 'all') {
    if (typeof WordCloud !== 'function') {
      element.innerHTML = '<p class="text-red-500 p-4">WordCloud ライブラリが読み込まれていません</p>'
      return
    }
    
    if (!wordData || wordData.length === 0) {
      element.innerHTML = '<p class="text-gray-500 p-4">表示するデータがありません</p>'
      return
    }
    
    // Canvas要素を作成（シンプルな方法）
    element.innerHTML = ''
    const canvas = document.createElement('canvas')
    
    // コンテナサイズを取得（整数値、スマホ対応で最小値を緩和）
    const rect = element.getBoundingClientRect()
    const displayWidth = Math.max(Math.round(rect.width) || 300, 300)
    const displayHeight = Math.max(Math.round(rect.height) || 250, 250)

    // 語と語の最小間隔(px)。詰まって読みにくいという要望で広げた(shrinkToFit で枠に収まるよう全体が縮む)
    // ビューポート幅ではなく canvas 幅で判定する。X 共起語@1440 は約 511px、Instagram@1440 は約 571px で中段に入る
    const gridSize = displayWidth < 360 ? 12 : (displayWidth < 700 ? 20 : 30)

    // wordcloud2 は ceil(幅/gridSize) 列のグリッドを作るので、幅が gridSize の倍数でないと
    // 最後の列・行が canvas の外にはみ出し、右端・下端の語が切れる。倍数に切り下げて防ぐ。
    // canvas は親の flex(items-center justify-center)で中央寄せされるため、数px縮んでも見た目は変わらない
    const fitWidth = Math.floor(displayWidth / gridSize) * gridSize
    const fitHeight = Math.floor(displayHeight / gridSize) * gridSize

    // 高DPI対応でジャギー解消。fitWidth は gridSize の倍数なので、dpr 倍した device 寸法は
    // そのまま「gridSize * dpr」(wordcloud2 に渡す実グリッド幅)の倍数になる
    const dpr = window.devicePixelRatio || 1
    const scaledWidth = Math.round(fitWidth * dpr)
    const scaledHeight = Math.round(fitHeight * dpr)

    // Canvas設定（高解像度）
    canvas.width = scaledWidth
    canvas.height = scaledHeight
    canvas.style.width = fitWidth + 'px'
    canvas.style.height = fitHeight + 'px'
    canvas.style.maxWidth = '100%'
    
    // CSS でアンチエイリアス強化
    canvas.style.fontSmooth = 'always'
    canvas.style.webkitFontSmoothing = 'antialiased'
    canvas.style.mozOsxFontSmoothing = 'grayscale'
    canvas.style.textRendering = 'optimizeLegibility'
    
    // コンテキスト設定（アンチエイリアス有効）
    // ctx.scale は使わない。wordcloud2 は canvas.width(device px)でグリッドを組み境界判定するため、
    // CSS px に scale すると判定と実際の描画位置がずれて端の語が切れる。
    // 代わりに gridSize と weightFactor を dpr 倍し、すべて device px で描く
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.textRenderingOptimization = 'optimizeQuality'
    
    element.appendChild(canvas)
    
    const options = this.wordCloudOptions(wordData, mainWord, sentimentColors, focus, {
      // 座標系は device px。gridSize / フォントサイズ(weightFactor)を dpr 倍して見た目の大きさを揃える
      gridSize, scale: dpr, canvasWidth: scaledWidth, canvasHeight: scaledHeight, isMobile: displayWidth < 600
    })

    // 文字の輪郭を描画して可読性を向上
    const styleId = 'word-cloud-inline-style'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        .word-cloud-text {
          paint-order: stroke fill;
          -webkit-text-stroke: 0.4px rgba(15, 23, 42, 0.2);
          text-stroke: 0.4px rgba(15, 23, 42, 0.2);
        }
      `
      document.head.appendChild(style)
    }

    // wordcloudstop は描き終わり(と打ち切り)で canvas に届く。描き直しで差し替えた古い canvas の通知では有効にしない
    canvas.addEventListener('wordcloudstop', () => {
      if (element.contains(canvas)) setDownloadReady(element, true)
    }, { once: true })

    try {
      WordCloud(canvas, options)
    } catch (error) {
      console.error('WordCloud render error:', error)
      element.innerHTML = '<p class="text-red-500 p-4">WordCloud エラー</p>'
    }
  }

  // wordcloud2 に渡す設定。画面表示と PNG 書き出し(オフスクリーンで描き直す)で共有する。
  // gridSize は論理 px、scale は論理 px → canvas px の倍率(画面は devicePixelRatio、書き出しは固定の 2 倍)
  wordCloudOptions(wordData, mainWord, sentimentColors, focus, { gridSize, scale, canvasWidth, canvasHeight, isMobile, forExport = false }) {
    // データ形式を確認して変換（品詞別色対応）
    let processedData = wordData
    if (wordData.length > 0 && wordData[0].length === 3) {
      // [word, count, color] 形式の場合
      processedData = wordData.map(([word, count]) => [word, count])
    }

    // 最小16pxを確保するためデータを前処理
    const maxWeight = Math.max(...processedData.map(item => item[1]))
    const minWeight = Math.min(...processedData.map(item => item[1]))

    // 重みを正規化（スマホでは小さめのフォントサイズ）
    const minFontSize = isMobile ? 10 : 16
    const maxFontSize = isMobile ? 30 : 60
    const normalizedData = processedData.map(([word, weight]) => {
      const normalizedWeight = maxWeight > minWeight ? (weight - minWeight) / (maxWeight - minWeight) : 0.5
      const newWeight = Math.round(minFontSize + (normalizedWeight * (maxFontSize - minFontSize)))
      return [word, newWeight]
    })

    return {
      list: normalizedData,
      gridSize: Math.round(gridSize * scale),
      weightFactor: scale,
      fontFamily: 'Noto Sans JP, system-ui, -apple-system, sans-serif',
      color: (word, weight) => this.getWordColor(word, weight, wordData, mainWord, sentimentColors, focus, forExport),
      rotateRatio: 0,
      // 同じ入力なら配置を固定する(色モードの切り替えでレイアウトが総入れ替えにならないように)
      shuffle: false,
      backgroundColor: 'transparent',
      drawOutOfBound: false,
      shrinkToFit: true,
      clearCanvas: false,
      origin: [Math.floor(canvasWidth / 2), Math.floor(canvasHeight / 2)],
      fontWeight: '600',
      classes: 'word-cloud-text'
    }
  }

  getWordColor(word, weight, wordData, mainWord, sentimentColors = null, focus = 'all', forExport = false) {
    const isMainWord = Boolean(mainWord) && word === mainWord

    // ポジ/ネガ表示は全語をその感情の単色で描く(「感情で色分け」トグルの状態は見ない)。
    // メインキーワードだけは赤がネガ色と衝突するため専用色で判別させる
    if (focus === 'positive' || focus === 'negative') {
      if (isMainWord) {
        // 書き出しは背景を白に固定するため、ダークテーマの白系のメイン色だと見えなくなる。ライトテーマの色に固定する
        if (forExport) return EXPORT_MAIN_WORD_COLOR
        return cssColor(MAIN_WORD_CSS_VARIABLE) || MAIN_WORD_COLOR
      }

      return sentimentColor(focus) || this.getGradientColor(weight, wordData)
    }

    // 全体表示: メインキーワードは感情色 ON でも赤のまま(凡例「キーワード自身」と対応)
    if (isMainWord) {
      return MAIN_WORD_COLOR // DaisyUI error color (赤)
    }

    // 感情色モードでは、内訳を持つ語だけ感情色にする(持たない語は従来どおり品詞色)
    if (colorMode === 'sentiment' && sentimentColors && sentimentColors[word]) {
      return sentimentColors[word]
    }

    // 品詞別色がある場合はそれを使用
    if (wordData.length > 0 && wordData[0].length === 3) {
      const wordItem = wordData.find(([w, count, color]) => w === word)
      if (wordItem && wordItem[2]) {
        // オリジナル品詞の場合も赤色にする
        if (wordItem[2] === '#d9534f') {
          return '#f31260'
        }
        return wordItem[2]
      }
    }
    
    // フォールバック: グラデーション色
    return this.getGradientColor(weight, wordData)
  }

  getGradientColor(weight, wordData) {
    // 最大・最小重みを取得
    const weights = wordData.map(item => item[1])
    const maxWeight = Math.max(...weights)
    const minWeight = Math.min(...weights)
    
    // 重みに基づく正規化（0-1の範囲）
    const normalizedWeight = (weight - minWeight) / (maxWeight - minWeight)
    
    // DaisyUIカラーの濃淡グラデーション（スコアが高いほど濃い色）
    if (normalizedWeight > 0.8) {
      return '#f31260' // 最高スコア: error (濃い赤)
    } else if (normalizedWeight > 0.6) {
      return '#f5a524' // 高スコア: warning (オレンジ)
    } else if (normalizedWeight > 0.4) {
      return '#167bff' // 中スコア: primary (青)
    } else if (normalizedWeight > 0.2) {
      return '#0bbf58' // 低スコア: success (緑)
    } else {
      return '#9c5de8' // 最低スコア: secondary (薄い紫)
    }
  }

  getDaisyUIColor() {
    // DaisyUIカラーパレット
    const colors = [
      '#f31260', // error (red)
      '#167bff', // primary (blue) 
      '#0bbf58', // success (green)
      '#f5a524', // warning (orange)
      '#14b4ff', // info (light blue)
      '#9c5de8', // secondary (purple)
      '#00d3bb', // accent (teal)
      '#A25772', // template-ui pink
      '#FB6D48', // template-ui orange
      '#8E7AB5'  // template-ui purple
    ]
    return colors[Math.floor(Math.random() * colors.length)]
  }

  // レスポンシブ対応：ウィンドウリサイズ時に再描画
  handleResize() {
    const wordclouds = document.querySelectorAll('[data-wordcloud]')
    wordclouds.forEach(element => {
      if (element.hasAttribute('data-wordcloud')) {
        // 少し遅延させて再描画(選択中の対象を保つ)
        setTimeout(() => {
          element.innerHTML = ''
          this.renderWordCloudFor(element)
        }, 300)
      }
    })
  }
}

// 自動初期化
const wordCloudRenderer = new WordCloudRenderer()

// レスポンシブ対応。リサイズのたびに new すると turbo:load リスナーが増えて N 重描画になるため、
// 生成済みのインスタンスを使い回す
let resizeTimer
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(() => {
    wordCloudRenderer.handleResize()
  }, 300)
})

// 「感情で色分け」トグルの切り替えを受けて描き直す(colorMode は描画時に DOM から取り直す)
document.addEventListener('text-mining:color-mode', () => {
  wordCloudRenderer.renderWordClouds()
})

// 対象の切替(全体 / ポジティブ / ネガティブ)。Turbo 遷移で二重登録しないよう document へのイベント委譲で処理する。
// セレクタを button に限定しないと、切替後の canvas コンテナ(data-wordcloud-focus を持つ)のクリックも拾ってしまう
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return

  const button = event.target.closest('button[data-wordcloud-focus]')
  if (!button) return

  const card = button.closest('.card-body') || document
  const element = card.querySelector('[data-wordcloud]')
  if (!element) return

  element.dataset.wordcloudFocus = button.getAttribute('data-wordcloud-focus')
  button.parentElement.querySelectorAll('button[data-wordcloud-focus]').forEach((other) => {
    const active = other === button
    other.classList.toggle('btn-active', active)
    other.setAttribute('aria-pressed', String(active))
  })
  wordCloudRenderer.renderWordCloudFor(element)
})

// wordcloud2 で canvas に描き、描き終わり(wordcloudstop)を待つ。
// EXPORT_TIMEOUT_MS までに届かなければ描きかけなので reject する(呼び出し側はダウンロードしない)
const drawWordCloud = (canvas, options) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('WordCloud export timed out')), EXPORT_TIMEOUT_MS)
  canvas.addEventListener('wordcloudstop', () => {
    clearTimeout(timer)
    resolve()
  }, { once: true })
  try {
    WordCloud(canvas, options)
  } catch (error) {
    clearTimeout(timer)
    reject(error)
  }
})

const downloadCanvas = (canvas, filename) => new Promise((resolve) => {
  canvas.toBlob((blob) => {
    if (blob) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 0)
    }
    resolve()
  }, 'image/png')
})

// ワードクラウドの PNG 保存。画面の canvas は devicePixelRatio しだいで解像度が変わり余白も大きいため、
// 同じ語リスト・設定でオフスクリーンの canvas に幅 1600px 相当で描き直し、語が描かれた範囲に余白を足して切り取る。
// 背景は資料に貼る用途のため白に固定する。ファイル名はチャートの CSV 名と同じく data-chart-export-*(対象名・識別子・SNS)と期間から組み立てる
const exportWordCloud = async (button) => {
  const card = button.closest('.card-body') || document
  const element = card.querySelector('[data-wordcloud]')
  if (!element || typeof WordCloud !== 'function') return

  const focus = element.dataset.wordcloudFocus || 'all'
  let wordData
  try {
    wordData = JSON.parse(wordCloudRenderer.dataFor(element, focus) || '[]')
  } catch (error) {
    console.error('WordCloud export data parsing error:', error)
    return
  }
  if (!Array.isArray(wordData) || wordData.length === 0) return

  button.disabled = true
  try {
    const rect = element.getBoundingClientRect()
    const layout = exportLayout({ width: rect.width, height: rect.height })
    const source = document.createElement('canvas')
    source.width = layout.canvasWidth
    source.height = layout.canvasHeight
    const options = wordCloudRenderer.wordCloudOptions(
      wordData, element.getAttribute('data-main-word'), wordCloudRenderer.sentimentColors(element), focus,
      { gridSize: layout.gridSize, scale: layout.scale, canvasWidth: layout.canvasWidth, canvasHeight: layout.canvasHeight, isMobile: false, forExport: true }
    )
    await drawWordCloud(source, options)

    const pixels = source.getContext('2d').getImageData(0, 0, source.width, source.height).data
    const frame = exportFrame(contentBounds(pixels, source.width, source.height), {
      padding: EXPORT_PADDING * layout.scale, width: source.width, height: source.height
    })
    const output = document.createElement('canvas')
    output.width = frame.width
    output.height = frame.height
    const context = output.getContext('2d')
    context.fillStyle = EXPORT_BACKGROUND
    context.fillRect(0, 0, output.width, output.height)
    context.drawImage(source, frame.sx, frame.sy, frame.sw, frame.sh, frame.dx, frame.dy, frame.sw, frame.sh)

    const scope = button.closest('[data-chart-export-entity]')
    const filename = buildWordcloudFilename({
      entity: scope?.dataset.chartExportEntity,
      identifier: scope?.dataset.chartExportIdentifier,
      platform: scope?.dataset.chartExportPlatform,
      focusLabel: focus === 'all' ? null : FOCUS_LABELS[focus],
      from: button.dataset.wordcloudDownloadFrom,
      to: button.dataset.wordcloudDownloadTo
    })
    await downloadCanvas(output, filename)
  } catch (error) {
    // タイムアウト・描画失敗ではダウンロードしない
    console.error('WordCloud export error:', error)
  } finally {
    // 成功・失敗どちらでもボタンを元の状態に戻す。書き出し中に対象を切り替えた場合などは画面側の状態(描画済みか)に合わせる
    button.disabled = element.dataset.wordcloudReady !== 'true'
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return

  const button = event.target.closest('button[data-wordcloud-download]')
  if (!button || button.disabled) return

  exportWordCloud(button)
})
